import {
    requiredFieldValidation,
    requiredFieldValidationWithLength,
    requiredFieldPhoneValidation,
} from "QuorumGrassroots/helperFunctions"

type FormValue = string | string[] | { length: number } | null | undefined
type ValidationFunction = (formValue: FormValue, fieldName?: string) => string | undefined
type FieldType = "phone_number" | "email" | string
type ValidatorDictionary = Record<string, ValidationFunction>

const { phone_number, email, text } = DjangIO.app.grassroots.types.FormInputType
const validatorCache: ValidatorDictionary = {}
const REQUIRED_ERROR_MESSAGES = new Set(["this field is required.", "this field is required"])

const optionalFieldValidationWithLength = (formValue: FormValue, fieldName?: string): string | undefined => {
    const maxLength = 255
    if (formValue && formValue.length > maxLength) {
        return fieldName
            ? `${fieldName} must be limited to ${maxLength} characters.`
            : `Please limit to ${maxLength} characters.`
    }
}

export const createCustomValidator = (validationFn: ValidationFunction, fieldLabel: string): ValidationFunction => {
    const cacheKey = `${validationFn.name}_${fieldLabel}`

    if (validatorCache[cacheKey]) {
        return validatorCache[cacheKey]
    }

    const customValidator: ValidationFunction = (value: FormValue): string | undefined => {
        const error = validationFn(value)
        if (error && REQUIRED_ERROR_MESSAGES.has(error.trim().toLowerCase())) {
            return `${fieldLabel} is required.`
        }
        return error
    }

    validatorCache[cacheKey] = customValidator

    return customValidator
}

export const validatorMap: ValidatorDictionary = {
    [phone_number.value]: requiredFieldPhoneValidation,
    [email.value]: requiredFieldValidationWithLength,
    [text.value]: requiredFieldValidationWithLength,
}

export const getCustomValidator = (
    fieldType: FieldType,
    fieldLabel: string,
    isRequired: boolean,
): ValidationFunction | undefined => {
    if (!isRequired) return undefined

    const baseValidator = validatorMap[fieldType] || requiredFieldValidation

    return createCustomValidator(baseValidator, fieldLabel)
}

export const getOptionalValidator = (fieldLabel: string): ValidationFunction =>
    createCustomValidator(optionalFieldValidationWithLength, fieldLabel)
